import { useState, useEffect, useRef, useMemo, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../../lib/api";
import { toast } from "sonner";
import {
  ArrowClockwise, CheckCircle, Warning, XCircle,
  CaretLeft, MagnifyingGlass, CaretDown, CaretUp, X,
  CheckFat, Sparkle, Info, FileText, ChartLine, Robot,
} from "@phosphor-icons/react";
import SportIcon from "../../components/SportIcon";
import LeagueLogo from "../../components/LeagueLogo";
import TeamLogo from "../../components/betiq/TeamLogo";
import { format } from "date-fns";
import { es } from "date-fns/locale";

/* ─── Constants ──────────────────────────────────────────────── */

const DEFAULT_STATS = {
  form_home: "", form_away: "", h2h_summary: "",
  home_advantage: 50, recent_goals_home: "", recent_goals_away: "",
  injuries: "", notes: "", key_factors: [], contextual_factors: [],
  // MLB pitcher fields
  pitcher_home_name: "", pitcher_home_era: "", pitcher_home_record: "", pitcher_home_whip: "", pitcher_home_era_f5: "",
  pitcher_away_name: "", pitcher_away_era: "", pitcher_away_record: "", pitcher_away_whip: "", pitcher_away_era_f5: "",
  h2h_last5: "",
};

const MARKETS_CONFIG = {
  football: [
    // --- Resultado del partido ---
    { id: "1X2",              label: "1X2 – Ganador",              type: "winner_1x2" },
    { id: "Doble oportunidad",label: "Doble oportunidad",          type: "double_chance" },
    { id: "Handicap",         label: "Hándicap asiático",          type: "handicap", lines: [-3, -2.5, -2, -1.5, -1, -0.5, 0.5, 1, 1.5, 2, 2.5, 3] },
    { id: "Resultado exacto", label: "Resultado exacto",           type: "exact_score", scores: ["1-0","0-0","1-1","2-0","2-1","0-1","2-2","3-0","3-1","0-2","3-2","0-3"] },
    // --- Goles ---
    { id: "Over/Under",       label: "Over / Under (Goles)",       type: "total", lines: [0.5, 1.5, 2.5, 3.5, 4.5, 5.5] },
    { id: "Ambos marcan",     label: "Ambos marcan (BTTS)",        type: "btts" },
    { id: "Primer gol",       label: "Equipo en marcar primero",   type: "winner_ml" },
    { id: "Gana al descanso y al final", label: "Gana 1ª mitad Y partido", type: "winner_1x2" },
    // --- Primera mitad ---
    { id: "1ª Mitad",         label: "Ganador 1ª mitad",           type: "half_winner", half: "1ª" },
    { id: "Over 1ª mitad",    label: "Over/Under goles 1ª mitad",  type: "total", lines: [0.5, 1.5, 2.5] },
    { id: "Handicap 1ª mitad",label: "Hándicap 1ª mitad",         type: "handicap", lines: [-1.5, -1, -0.5, 0.5, 1, 1.5] },
    // --- Segunda mitad ---
    { id: "2ª Mitad",         label: "Ganador 2ª mitad",           type: "half_winner", half: "2ª" },
    { id: "Over 2ª mitad",    label: "Over/Under goles 2ª mitad",  type: "total", lines: [0.5, 1.5, 2.5] },
    // --- Estadísticas ---
    { id: "Córners",          label: "Córners totales",            type: "total", lines: [7.5, 8.5, 9.5, 10.5, 11.5, 12.5] },
    { id: "Córners 1ª mitad", label: "Córners 1ª mitad",          type: "total", lines: [3.5, 4.5, 5.5, 6.5] },
    { id: "Total tarjetas",   label: "Total tarjetas",             type: "total", lines: [1.5, 2.5, 3.5, 4.5, 5.5] },
    { id: "Disparos totales", label: "Disparos al arco totales",   type: "total", lines: [18.5, 20.5, 22.5, 24.5, 26.5] },
    { id: "Sin goles 2ª mitad", label: "Sin goles en 2ª mitad",   type: "yn" },
    // --- Mercados extra ---
    { id: "DNB",              label: "Empate sin apuesta (DNB)",   type: "winner_ml" },
    { id: "Hándicap europeo", label: "Hándicap europeo",           type: "handicap", lines: [-2, -1, 0, 1, 2] },
    { id: "Portería a cero",  label: "Portería a cero",            type: "winner_ml" },
    { id: "Habrá penalti",    label: "Habrá penalti en el partido",type: "yn" },
    { id: "Tarjeta roja",     label: "Habrá tarjeta roja",         type: "yn" },
    { id: "Goles exactos",    label: "Total goles exactos",        type: "exact_score", scores: ["0","1","2","3","4","5+"] },
  ],
  nba: [
    // --- Resultado ---
    { id: "Moneyline",        label: "Moneyline – Ganador partido",type: "winner_ml" },
    { id: "Spread",           label: "Spread partido",             type: "handicap", lines: [-15.5, -12.5, -10.5, -8.5, -7.5, -5.5, -4.5, -3.5, -2.5, -1.5, 1.5, 2.5, 3.5, 4.5, 5.5, 7.5, 8.5, 10.5, 12.5, 15.5] },
    { id: "Over/Under 215.5", label: "Total puntos partido",       type: "total", lines: [195.5, 200.5, 205.5, 210.5, 215.5, 220.5, 225.5, 230.5, 235.5, 240.5] },
    { id: "Margen victoria",  label: "Margen de victoria",         type: "margin", ranges: ["1-5 pts","6-10 pts","11-15 pts","16-20 pts","21+ pts"] },
    // --- Primera mitad ---
    { id: "1ª Mitad NBA",     label: "Ganador 1ª mitad",           type: "winner_ml" },
    { id: "Spread 1ª mitad NBA", label: "Spread 1ª mitad",        type: "handicap", lines: [-8.5, -6.5, -4.5, -3.5, -2.5, -1.5, 1.5, 2.5, 3.5, 4.5, 6.5, 8.5] },
    { id: "Total 1ª mitad NBA", label: "Total puntos 1ª mitad",   type: "total", lines: [95.5, 100.5, 105.5, 110.5, 115.5, 120.5, 125.5] },
    // --- 1er cuarto ---
    { id: "1er cuarto NBA",   label: "Ganador 1er cuarto",         type: "winner_ml" },
    { id: "Spread 1Q NBA",    label: "Spread 1er cuarto",          type: "handicap", lines: [-5.5, -4.5, -3.5, -2.5, -1.5, 1.5, 2.5, 3.5, 4.5, 5.5] },
    { id: "Total 1Q NBA",     label: "Total puntos 1er cuarto",    type: "total", lines: [46.5, 48.5, 50.5, 52.5, 54.5, 56.5, 58.5] },
    // --- 2do cuarto ---
    { id: "2do cuarto NBA",   label: "Ganador 2do cuarto",         type: "winner_ml" },
    { id: "Spread 2Q NBA",    label: "Spread 2do cuarto",          type: "handicap", lines: [-5.5, -4.5, -3.5, -2.5, -1.5, 1.5, 2.5, 3.5, 4.5, 5.5] },
    { id: "Total 2Q NBA",     label: "Total puntos 2do cuarto",    type: "total", lines: [46.5, 48.5, 50.5, 52.5, 54.5, 56.5] },
    // --- 3er cuarto ---
    { id: "3er cuarto NBA",   label: "Ganador 3er cuarto",         type: "winner_ml" },
    { id: "Total 3Q NBA",     label: "Total puntos 3er cuarto",    type: "total", lines: [46.5, 48.5, 50.5, 52.5, 54.5, 56.5] },
    // --- 4to cuarto ---
    { id: "4to cuarto NBA",   label: "Ganador 4to cuarto",         type: "winner_ml" },
    { id: "Total 4Q NBA",     label: "Total puntos 4to cuarto",    type: "total", lines: [46.5, 48.5, 50.5, 52.5, 54.5, 56.5] },
    // --- Props de equipo/jugador ---
    { id: "Total rebotes",    label: "Total rebotes partido",      type: "total", lines: [85.5, 90.5, 95.5, 100.5, 105.5, 110.5] },
    { id: "Total asistencias",label: "Total asistencias partido",  type: "total", lines: [40.5, 43.5, 46.5, 49.5, 52.5, 55.5] },
    { id: "Primer anotador",  label: "Primer equipo en anotar",    type: "winner_ml" },
    { id: "Triple-doble",     label: "Ocurre triple-doble",        type: "yn" },
    { id: "Jugador pts",      label: "Jugador +25.5 puntos",       type: "player_ou", line: 25.5 },
    { id: "Jugador pts 20",   label: "Jugador +20.5 puntos",       type: "player_ou", line: 20.5 },
    { id: "Jugador pts 30",   label: "Jugador +30.5 puntos",       type: "player_ou", line: 30.5 },
    { id: "Jugador rebotes",  label: "Jugador +9.5 rebotes",       type: "player_ou", line: 9.5 },
    { id: "Jugador asistencias", label: "Jugador +7.5 asistencias", type: "player_ou", line: 7.5 },
  ],
  mlb: [
    // --- Resultado del partido ---
    { id: "Moneyline",           label: "Moneyline – Ganador",               type: "winner_ml" },
    { id: "Run Line",            label: "Run Line (±1.5)",                   type: "handicap", lines: [-2.5, -1.5, -0.5, 0.5, 1.5, 2.5] },
    { id: "Alt Run Line",        label: "Run Line Alternativo",              type: "handicap", lines: [-4.5, -3.5, -2.5, -1.5, -0.5, 0.5, 1.5, 2.5, 3.5, 4.5] },
    { id: "Over/Under",          label: "Total carreras partido",            type: "total", lines: [5.5, 6.5, 7.5, 8.5, 9.5, 10.5, 11.5, 12.5, 13.5] },
    // --- Totales por equipo (Team Totals) ---
    { id: "Team Total Local",    label: "Total carreras equipo local",       type: "total", lines: [2.5, 3.5, 4.5, 5.5, 6.5, 7.5] },
    { id: "Team Total Visitante",label: "Total carreras equipo visitante",   type: "total", lines: [2.5, 3.5, 4.5, 5.5, 6.5, 7.5] },
    { id: "Ambos anotan",        label: "Ambos equipos anotan",              type: "btts" },
    { id: "Extra innings",       label: "Va a extra innings",                type: "yn" },
    // --- Primeros 5 innings (F5) ---
    { id: "F5 innings",          label: "Gana primeros 5 innings (F5)",      type: "winner_ml" },
    { id: "F5 Over/Under",       label: "Total carreras F5",                 type: "total", lines: [2.5, 3.5, 4.5, 5.5, 6.5, 7.5] },
    { id: "F5 Run Line",         label: "Run Line F5 (±1.5)",               type: "handicap", lines: [-1.5, -0.5, 0.5, 1.5] },
    { id: "F5 Ambos anotan",     label: "Ambos equipos anotan en F5",        type: "btts" },
    { id: "F5 Team Total Local", label: "Total carreras local en F5",        type: "total", lines: [1.5, 2.5, 3.5, 4.5] },
    { id: "F5 Team Total Visit", label: "Total carreras visitante en F5",    type: "total", lines: [1.5, 2.5, 3.5, 4.5] },
    // --- 1er inning (NRFI / YRFI) ---
    { id: "NRFI",                label: "NRFI – Sin carrera 1er inning",     type: "yn" },
    { id: "YRFI",                label: "YRFI – Con carrera 1er inning",     type: "yn" },
    { id: "1er inning",          label: "Equipo que anota en 1er inning",    type: "winner_ml" },
    { id: "Total 1er inning",    label: "Total carreras 1er inning",         type: "total", lines: [0.5, 1.5, 2.5] },
    // --- Innings específicos ---
    { id: "Total 3er inning",    label: "Total carreras 3er inning",         type: "total", lines: [0.5, 1.5, 2.5] },
    { id: "Total 5to inning",    label: "Total carreras 5to inning",         type: "total", lines: [0.5, 1.5, 2.5] },
    { id: "Total 7mo inning",    label: "Total carreras 7mo inning",         type: "total", lines: [0.5, 1.5, 2.5] },
    { id: "Innings ganados",     label: "Equipo que gana más innings",       type: "winner_ml" },
    // --- Pitcher props ---
    { id: "Strikeouts",          label: "Ponches abridor local (K's)",    type: "total", lines: [3.5, 4.5, 5.5, 6.5, 7.5, 8.5, 9.5] },
    { id: "Strikeouts visitante",label: "Ponches abridor visitante (K's)",type: "total", lines: [3.5, 4.5, 5.5, 6.5, 7.5, 8.5, 9.5] },
    { id: "Strikeouts combo",    label: "K's combinados (ambos abridores)",  type: "total", lines: [8.5, 10.5, 12.5, 14.5, 16.5, 18.5] },
    { id: "Innings lanzados 6",  label: "Pitcher lanza 6+ innings",          type: "yn" },
    { id: "Innings lanzados 5",  label: "Pitcher lanza 5+ innings",          type: "yn" },
    { id: "Walks pitcher",       label: "Walks del abridor (BB)",            type: "total", lines: [1.5, 2.5, 3.5, 4.5] },
    { id: "Hits permitidos",     label: "Hits permitidos por abridor",       type: "total", lines: [3.5, 4.5, 5.5, 6.5, 7.5] },
    { id: "Earned runs",         label: "Carreras limpias del abridor (ER)", type: "total", lines: [1.5, 2.5, 3.5, 4.5] },
    // --- Ofensiva equipo ---
    { id: "Total hits",          label: "Total hits partido",                type: "total", lines: [5.5, 6.5, 7.5, 8.5, 9.5, 10.5, 11.5] },
    { id: "Home runs",           label: "Home runs totales",                 type: "total", lines: [0.5, 1.5, 2.5, 3.5] },
    { id: "Home run partido",    label: "Habrá home run en el partido",      type: "yn" },
    { id: "Grand Slam",          label: "Habrá grand slam",                  type: "yn" },
    { id: "Total walks partido", label: "Total walks (BB) partido",          type: "total", lines: [4.5, 5.5, 6.5, 7.5, 8.5] },
    { id: "Total errores",       label: "Total errores en el partido",       type: "total", lines: [0.5, 1.5, 2.5, 3.5] },
    { id: "R+H+E",               label: "Total R+H+E (carreras+hits+errores)", type: "total", lines: [18.5, 20.5, 22.5, 24.5, 26.5, 28.5] },
    { id: "Bases robadas",       label: "Total bases robadas partido",       type: "total", lines: [0.5, 1.5, 2.5, 3.5] },
    // --- Player props ---
    { id: "Jugador hits 0.5",    label: "Jugador anota al menos 1 hit",      type: "player_ou", line: 0.5 },
    { id: "Jugador hits",        label: "Jugador over 1.5 hits",             type: "player_ou", line: 1.5 },
    { id: "Jugador hits 2.5",    label: "Jugador over 2.5 hits",             type: "player_ou", line: 2.5 },
    { id: "Jugador TB 1.5",      label: "Jugador Total Bases over 1.5",      type: "player_ou", line: 1.5 },
    { id: "Jugador TB 2.5",      label: "Jugador Total Bases over 2.5",      type: "player_ou", line: 2.5 },
    { id: "Jugador TB 3.5",      label: "Jugador Total Bases over 3.5",      type: "player_ou", line: 3.5 },
    { id: "Jugador RBI 0.5",     label: "Jugador anota al menos 1 RBI",      type: "player_ou", line: 0.5 },
    { id: "Jugador RBI 1.5",     label: "Jugador over 1.5 RBIs",             type: "player_ou", line: 1.5 },
    { id: "Jugador carrera",     label: "Jugador anota una carrera (run)",   type: "player_ou", line: 0.5 },
    { id: "Jugador HR",          label: "Jugador anota home run",            type: "yn" },
    { id: "Jugador Ks",          label: "Jugador (bateador) 2+ ponches",     type: "player_ou", line: 1.5 },
    // --- Primero en llegar ---
    { id: "Primera carrera",     label: "Equipo que anota primero",          type: "winner_ml" },
    { id: "Primero 3 carreras",  label: "Primero en llegar a 3 carreras",    type: "winner_ml" },
    // --- Resultados especiales ---
    { id: "Resultado exacto MLB",label: "Resultado exacto",                  type: "exact_score", scores: ["1-0","2-0","2-1","3-0","3-1","3-2","4-1","4-2","5-3","6-3","7-3","8-4","4-0","5-2","6-2","7-4"] },
  ],
  nfl: [
    // --- Resultado ---
    { id: "Moneyline",        label: "Moneyline – Ganador",        type: "winner_ml" },
    { id: "Spread NFL",       label: "Spread partido",             type: "handicap", lines: [-14.5, -10.5, -7.5, -5.5, -3.5, -1.5, 1.5, 3.5, 5.5, 7.5, 10.5, 14.5] },
    { id: "Over/Under NFL",   label: "Total puntos partido",       type: "total", lines: [38.5, 41.5, 44.5, 47.5, 50.5, 53.5, 56.5] },
    // --- Por mitad ---
    { id: "Primera mitad NFL",label: "Ganador 1ª mitad",           type: "winner_ml" },
    { id: "Spread 1H NFL",    label: "Spread 1ª mitad",            type: "handicap", lines: [-7.5, -5.5, -3.5, -1.5, 1.5, 3.5, 5.5, 7.5] },
    { id: "Total 1H NFL",     label: "Total puntos 1ª mitad",      type: "total", lines: [18.5, 20.5, 22.5, 24.5, 26.5, 28.5] },
    { id: "2ª mitad NFL",     label: "Ganador 2ª mitad",           type: "winner_ml" },
    // --- Por cuarto ---
    { id: "1Q NFL",           label: "Ganador 1er cuarto",         type: "winner_ml" },
    { id: "Total 1Q NFL",     label: "Total puntos 1er cuarto",    type: "total", lines: [8.5, 9.5, 10.5, 11.5, 12.5, 13.5] },
    // --- Puntuación especial ---
    { id: "Primer touchdown", label: "Equipo en anotar primero",   type: "winner_ml" },
    { id: "Total touchdowns", label: "Total touchdowns",           type: "total", lines: [3.5, 4.5, 5.5, 6.5, 7.5] },
    { id: "Margen NFL",       label: "Margen de victoria",         type: "margin", ranges: ["1-3 pts","4-7 pts","8-14 pts","15-21 pts","22+ pts"] },
    { id: "Ambos anotan NFL", label: "Ambos equipos anotan TD",    type: "btts" },
  ],
  nhl: [
    // --- Resultado ---
    { id: "Moneyline NHL",    label: "Moneyline – Ganador",        type: "winner_ml" },
    { id: "Puck line",        label: "Puck line (±1.5)",           type: "handicap", lines: [-1.5, 1.5] },
    { id: "Over/Under NHL",   label: "Total goles partido",        type: "total", lines: [4.5, 5.5, 6.5, 7.5, 8.5] },
    { id: "Doble oportunidad NHL", label: "Doble oportunidad",     type: "double_chance" },
    // --- Por período ---
    { id: "1er período",      label: "Ganador 1er período",        type: "winner_ml" },
    { id: "Total 1P NHL",     label: "Total goles 1er período",    type: "total", lines: [0.5, 1.5, 2.5] },
    { id: "2do período NHL",  label: "Ganador 2do período",        type: "winner_ml" },
    { id: "Total 2P NHL",     label: "Total goles 2do período",    type: "total", lines: [0.5, 1.5, 2.5] },
    { id: "3er período NHL",  label: "Ganador 3er período",        type: "winner_ml" },
    // --- Goles especiales ---
    { id: "Primer gol NHL",   label: "Equipo en marcar primero",   type: "winner_ml" },
    { id: "Ambos anotan NHL", label: "Ambos equipos anotan",       type: "btts" },
    { id: "Total hits NHL",   label: "Total hits del partido",     type: "total", lines: [40.5, 45.5, 50.5, 55.5, 60.5] },
    { id: "Va a OT",          label: "Va a tiempo extra (OT)",     type: "yn" },
    { id: "Shutout",          label: "Shutout (sin goles)",        type: "yn" },
    { id: "Margen NHL",       label: "Margen de victoria",         type: "margin", ranges: ["1 gol","2 goles","3+ goles"] },
  ],
  tennis: [
    // --- Resultado ---
    { id: "Ganador partido",  label: "Ganador del partido (ML)",   type: "winner_ml" },
    { id: "Hándicap sets",    label: "Hándicap por sets (±1.5)",  type: "handicap", lines: [-1.5, 1.5] },
    { id: "Total sets",       label: "Total sets (over/under 2.5)",type: "total", lines: [2.5] },
    { id: "Marcador sets",    label: "Resultado exacto en sets",   type: "exact_score", scores: ["2-0","2-1","0-2","1-2"] },
    // --- Juegos ---
    { id: "Total juegos",     label: "Total juegos del partido",   type: "total", lines: [18.5, 19.5, 20.5, 21.5, 22.5, 23.5, 24.5, 25.5] },
    { id: "Total juegos set 1",label: "Total juegos 1er set",      type: "total", lines: [8.5, 9.5, 10.5, 11.5, 12.5] },
    { id: "Total juegos set 2",label: "Total juegos 2do set",      type: "total", lines: [8.5, 9.5, 10.5, 11.5, 12.5] },
    // --- Por set ---
    { id: "Ganador set 1",    label: "Ganador 1er set",            type: "winner_ml" },
    { id: "Ganador set 2",    label: "Ganador 2do set",            type: "winner_ml" },
    { id: "Ganador set 3",    label: "Ganador 3er set",            type: "winner_ml" },
    // --- Especiales ---
    { id: "Tiebreak",         label: "Habrá tiebreak en el partido",type: "yn" },
    { id: "Tiebreak set 1",   label: "Habrá tiebreak en 1er set", type: "yn" },
    { id: "Doble falta",      label: "Over dobles faltas partido", type: "total", lines: [3.5, 4.5, 5.5, 6.5] },
    { id: "Aces totales",     label: "Over aces totales partido",  type: "total", lines: [8.5, 10.5, 12.5, 15.5, 18.5] },
  ],
};

const FOOTBALL_LEAGUES = [
  { key: "laliga",  label: "LaLiga" },
  { key: "ucl",     label: "Champions" },
  { key: "premier", label: "Premier" },
];
const SPORT_DISPLAY = {
  football: { label: "Fútbol", iconSize: 64, leagues: FOOTBALL_LEAGUES },
  nba:      { label: "NBA",    iconSize: 64 },
  mlb:      { label: "MLB",    iconSize: 64 },
  nfl:      { label: "NFL",    iconSize: 64 },
  nhl:      { label: "NHL",    iconSize: 64 },
  tennis:   { label: "ATP",    iconSize: 64 },
};

const LABEL_MAP = { strong: "Sólida", good: "Buena", questionable: "Dudosa", weak: "Débil" };

const TYPICAL_ODDS = {
  football: {
    "1X2":               "1.40 – 3.50",
    "Over/Under":        "1.70 – 2.10",
    "Doble oportunidad": "1.20 – 1.80",
    "Ambos marcan":      "1.65 – 2.20",
    "Handicap":          "1.70 – 2.20",
    "1ª Mitad":          "1.70 – 3.20",
    "2ª Mitad":          "1.70 – 3.20",
    "Over 1ª mitad":     "1.75 – 2.05",
    "Resultado exacto":  "5.00 – 20.00",
    "Córners":           "1.80 – 2.00",
    "Primer gol":        "1.60 – 2.50",
  },
  nba: {
    "Moneyline":           "1.50 – 2.80",
    "Spread":              "1.85 – 1.95",
    "Over/Under 215.5":    "1.85 – 1.95",
    "1ª Mitad NBA":        "1.85 – 1.95",
    "Spread 1ª mitad NBA": "1.85 – 1.95",
    "Total 1ª mitad NBA":  "1.85 – 1.95",
    "1er cuarto NBA":      "1.85 – 1.95",
    "Spread 1Q NBA":       "1.85 – 1.95",
    "Total 1Q NBA":        "1.85 – 1.95",
    "2do cuarto NBA":      "1.85 – 1.95",
    "Total 2Q NBA":        "1.85 – 1.95",
    "Jugador pts":         "1.85 – 1.95",
    "Jugador pts 20":      "1.85 – 1.95",
    "Jugador pts 30":      "1.85 – 1.95",
    "Jugador rebotes":     "1.85 – 1.95",
  },
  mlb: {
    "Moneyline":           "1.60 – 2.80",
    "Run Line":            "1.70 – 2.20",
    "Alt Run Line":        "1.50 – 2.80",
    "Over/Under":          "1.75 – 2.05",
    "Team Total Local":    "1.80 – 2.00",
    "Team Total Visitante":"1.80 – 2.00",
    "F5 innings":          "1.75 – 2.10",
    "F5 Over/Under":       "1.75 – 2.05",
    "F5 Run Line":         "1.80 – 2.00",
    "NRFI":                "1.70 – 2.20",
    "YRFI":                "1.70 – 2.20",
    "Strikeouts":          "1.85 – 1.95",
    "Strikeouts visitante":"1.85 – 1.95",
    "Strikeouts combo":    "1.85 – 1.95",
    "Walks pitcher":       "1.85 – 1.95",
    "Home runs":           "1.85 – 1.95",
    "Total hits":          "1.80 – 2.00",
    "Jugador TB 1.5":      "1.70 – 2.10",
    "Jugador TB 2.5":      "1.85 – 2.05",
    "Jugador RBI 0.5":     "1.60 – 2.00",
  },
  nfl: {
    "Moneyline":       "1.60 – 2.60",
    "Spread NFL":      "1.85 – 1.95",
    "Over/Under NFL":  "1.85 – 1.95",
    "Primera mitad NFL":"1.85 – 1.95",
    "Total touchdowns":"1.85 – 1.95",
  },
  nhl: {
    "Moneyline NHL":   "1.60 – 2.60",
    "Puck line":       "1.80 – 2.00",
    "Over/Under NHL":  "1.85 – 1.95",
    "1er período":     "1.85 – 1.95",
  },
};

// Helper: compute form score (0..1) from "WWLDW"
function _formScoreJS(form) {
  if (!form) return 0.5;
  const map = { W: 1.0, D: 0.5, L: 0.0 };
  const chars = form.toUpperCase().split("").filter(c => map[c] !== undefined);
  if (!chars.length) return 0.5;
  const weights = chars.map((_, i) => 1 + i * 0.15);
  const weighted = chars.reduce((s, c, i) => s + (map[c] || 0) * weights[i], 0);
  return weighted / weights.reduce((s, w) => s + w, 0);
}

const CONTEXTUAL_FACTORS = [
  { id: "injury_away",     label: "Lesiones importantes en equipo visitante" },
  { id: "injury_home",     label: "Lesiones importantes en equipo local" },
  { id: "positive_streak", label: "Equipo local en racha positiva" },
  { id: "must_win",        label: "Partido crucial para clasificación" },
  { id: "good_rest",       label: "Descanso de 3+ días (ventaja física)" },
  { id: "weather_favor",   label: "Condiciones climáticas favorables" },
];

/* ─── Main component ─────────────────────────────────────────── */

export default function AdminCreatePick() {
  const nav = useNavigate();

  // Core pick state
  const [sport, setSport]             = useState("football");
  const [leagueFilter, setLeagueFilter] = useState(null); // laliga | ucl | premier | null
  const [sportConfirmed, setSportConfirmed] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [market, setMarket]           = useState("1X2");
  const [selectionData, setSelectionData] = useState({});
  const [confidence, setConfidence]   = useState(5);   // Tipster conviction 1-10
  const [units, setUnits]             = useState(3);    // Units to bet 1-10
  const [analysis, setAnalysis]       = useState("");
  const [stats, setStats]             = useState({ ...DEFAULT_STATS });

  // UI state
  const [preview, setPreview]         = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [saving, setSaving]           = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [marketConfirmed, setMarketConfirmed] = useState(false);
  const [newFactor, setNewFactor]     = useState("");
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsLoaded, setStatsLoaded]   = useState(false);

  // Monte Carlo state
  const [mcSim, setMcSim]         = useState(null);
  const [mcLoading, setMcLoading] = useState(false);

  const marketConfig = useMemo(
    () => (MARKETS_CONFIG[sport] || []).find(m => m.id === market) || MARKETS_CONFIG[sport][0],
    [sport, market]
  );

  // Reset market when sport changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const first = MARKETS_CONFIG[sport][0];
    setMarket(first.id);
    setSelectionData({});
    setMarketConfirmed(false);
    resetAnalysis();
  }, [sport]);

  // Reset selection when market changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setSelectionData({});
    resetAnalysis();
  }, [market]);

  // Auto-fill analysis from AI summary
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (preview?.ai_validation?.summary && !analysis) {
      setAnalysis(preview.ai_validation.summary);
    }
  }, [preview]);

  // Auto-fetch real stats when a match is selected
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!selectedEvent) { setStatsLoaded(false); return; }
    setStatsLoaded(false);
    if (!selectedEvent.home_team_id && !selectedEvent.away_team_id) return;
    setStatsLoading(true);
    const params = new URLSearchParams({
      home_team_id: selectedEvent.home_team_id || "",
      away_team_id: selectedEvent.away_team_id || "",
      league: selectedEvent.league || "",
    });
    apiClient
      .get(`/admin/events/${selectedEvent.sport}/${selectedEvent.id}/stats?${params}`)
      .then(res => {
        const d = res.data;
        if (d && Object.keys(d).length > 0) {
          setStats(prev => ({
            ...prev,
            ...(d.home_form            ? { form_home: d.home_form }                   : {}),
            ...(d.away_form            ? { form_away: d.away_form }                   : {}),
            ...(d.home_ppg   != null   ? { recent_goals_home: d.home_ppg }            : {}),
            ...(d.away_ppg   != null   ? { recent_goals_away: d.away_ppg }            : {}),
            ...(d.h2h_summary          ? { h2h_summary: d.h2h_summary }               : {}),
            ...(d.home_record          ? { home_record: d.home_record }               : {}),
            ...(d.away_record          ? { away_record: d.away_record }               : {}),
          }));
          setStatsLoaded(true);
        }
      })
      .catch(() => {})
      .finally(() => setStatsLoading(false));
  }, [selectedEvent?.id]);

  // Auto-fetch MLB pitcher data when MLB match is selected — and SIEMPRE abrir acordeón
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!selectedEvent || selectedEvent.sport !== "mlb") return;
    setShowAdvanced(true);  // ← SIEMPRE expandir cuando es MLB para que admin vea la sección
    fetchMlbPitchers();
  }, [selectedEvent?.id]);

  const [mlbFetching, setMlbFetching] = useState(false);
  const [mlbFetched, setMlbFetched]   = useState(false);

  const fetchMlbPitchers = async () => {
    if (!selectedEvent) return;
    setMlbFetching(true);
    setMlbFetched(false);
    try {
      const matchDate = selectedEvent.match_date
        ? selectedEvent.match_date.split("T")[0]
        : new Date().toISOString().split("T")[0];
      const params = new URLSearchParams({
        home_team: selectedEvent.home_team,
        away_team: selectedEvent.away_team,
        game_date: matchDate,
      });
      const res = await apiClient.get(`/admin/mlb/pitcher-stats?${params}`);
      const d = res.data;
      if (d && !d.error) {
        setStats(prev => ({
          ...prev,
          pitcher_home_name:   d.pitcher_home?.name   || prev.pitcher_home_name,
          pitcher_home_era:    d.pitcher_home?.era     ?? prev.pitcher_home_era,
          pitcher_home_record: d.pitcher_home?.record  || prev.pitcher_home_record,
          pitcher_home_whip:   d.pitcher_home?.whip    ?? prev.pitcher_home_whip,
          pitcher_away_name:   d.pitcher_away?.name   || prev.pitcher_away_name,
          pitcher_away_era:    d.pitcher_away?.era     ?? prev.pitcher_away_era,
          pitcher_away_record: d.pitcher_away?.record  || prev.pitcher_away_record,
          pitcher_away_whip:   d.pitcher_away?.whip    ?? prev.pitcher_away_whip,
          h2h_last5:           d.h2h_last5             || prev.h2h_last5,
        }));
        setMlbFetched(true);
        setShowAdvanced(true);
        if (d.tba || d.warning) {
          toast.warning(d.warning || "Pitchers aún no anunciados (TBA). Vuelve a cargar más cerca del partido.");
        } else if (d.pitcher_home?.name || d.pitcher_away?.name) {
          toast.success(`✅ Pitchers cargados: ${d.pitcher_home?.name || "?"} vs ${d.pitcher_away?.name || "?"}`);
        } else {
          toast.info("MLB Stats API consultada — sin pitchers anunciados aún");
        }
      } else {
        toast.error(d?.error || "No se encontraron pitchers probables para este partido");
      }
    } catch {
      toast.error("Error al obtener datos de MLB");
    } finally {
      setMlbFetching(false);
    }
  };

  const resetAnalysis = () => {
    setPreview(null);
    setAnalysis("");
  };

  // pickSelection must be declared BEFORE the Monte Carlo useEffect
  const pickSelection = useMemo(() => {
    if (!selectedEvent || !marketConfig) return "";
    const home = selectedEvent.home_team;
    const away = selectedEvent.away_team;
    const t = marketConfig.type;
    const d = selectionData;
    if (t === "winner_1x2") {
      if (d.pick === "home") return home;
      if (d.pick === "draw") return "Empate";
      if (d.pick === "away") return away;
    }
    if (t === "winner_ml") {
      if (d.pick === "home") return home;
      if (d.pick === "away") return away;
    }
    if (t === "double_chance") {
      if (d.pick === "1X") return `${home} o Empate`;
      if (d.pick === "12") return `${home} o ${away}`;
      if (d.pick === "X2") return `Empate o ${away}`;
    }
    if (t === "total") {
      if (d.direction && d.line != null) return `${d.direction === "over" ? "Más de" : "Menos de"} ${d.line}`;
    }
    if (t === "btts") {
      if (d.pick === "yes") return "Ambos marcan: Sí";
      if (d.pick === "no") return "Ambos marcan: No";
    }
    if (t === "handicap") {
      if (d.team && d.line != null) {
        const teamName = d.team === "home" ? home : away;
        const sign = d.line > 0 ? "+" : "";
        return `${teamName} ${sign}${d.line}`;
      }
    }
    if (t === "half_winner") {
      const ctx = `(${marketConfig.half} mitad)`;
      if (d.pick === "home") return `${home} ${ctx}`;
      if (d.pick === "draw") return `Empate ${ctx}`;
      if (d.pick === "away") return `${away} ${ctx}`;
    }
    if (t === "exact_score") {
      if (d.score) return d.score;
    }
    if (t === "yn") {
      if (d.pick === "yes") return "Sí";
      if (d.pick === "no") return "No";
    }
    if (t === "margin") {
      if (d.range) return d.range;
    }
    if (t === "player_ou") {
      const line = marketConfig.line || 25.5;
      if (d.direction === "over") return `Over ${line} pts`;
      if (d.direction === "under") return `Under ${line} pts`;
    }
    return "";
  }, [selectedEvent, marketConfig, selectionData]);

  // Auto-run Monte Carlo when match is selected OR pick selection changes
  useEffect(() => {
    if (!selectedEvent) { setMcSim(null); return; }
    setMcLoading(true);
    setMcSim(null);
    const params = new URLSearchParams({
      sport: sport || "",
      league: selectedEvent.league || "",
      home_team: selectedEvent.home_team || "",
      away_team: selectedEvent.away_team || "",
      pick_selection: pickSelection || "",
    });
    apiClient
      .get(`/public/analysis/simulate?${params}`)
      .then(res => setMcSim(res.data))
      .catch(() => setMcSim(null))
      .finally(() => setMcLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEvent?.id, pickSelection]);

  const canPreview = !!(selectedEvent && pickSelection);

  const buildStatsPayload = () => ({
    ...stats,
    recent_goals_home:  stats.recent_goals_home  ? parseFloat(stats.recent_goals_home)  : null,
    recent_goals_away:  stats.recent_goals_away  ? parseFloat(stats.recent_goals_away)  : null,
    home_advantage:     parseInt(stats.home_advantage) || 50,
    // MLB pitcher numeric conversions
    pitcher_home_era:   stats.pitcher_home_era   !== "" ? parseFloat(stats.pitcher_home_era)   : null,
    pitcher_home_whip:  stats.pitcher_home_whip  !== "" ? parseFloat(stats.pitcher_home_whip)  : null,
    pitcher_home_era_f5:stats.pitcher_home_era_f5!== "" ? parseFloat(stats.pitcher_home_era_f5): null,
    pitcher_away_era:   stats.pitcher_away_era   !== "" ? parseFloat(stats.pitcher_away_era)   : null,
    pitcher_away_whip:  stats.pitcher_away_whip  !== "" ? parseFloat(stats.pitcher_away_whip)  : null,
    pitcher_away_era_f5:stats.pitcher_away_era_f5!== "" ? parseFloat(stats.pitcher_away_era_f5): null,
  });

  const runPreview = async () => {
    if (!canPreview) {
      toast.error("Selecciona un partido, mercado y configura la jugada primero");
      return;
    }
    setLoadingPreview(true);
    setPreview(null);
    try {
      const res = await apiClient.post("/admin/scoring-preview", {
        sport, home_team: selectedEvent.home_team, away_team: selectedEvent.away_team,
        market, pick_selection: pickSelection, stats: buildStatsPayload(),
      });
      setPreview(res.data);
      toast.success("Evaluación completada");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error en la evaluación");
    } finally {
      setLoadingPreview(false);
    }
  };

  const onSave = async () => {
    const finalAnalysis = analysis || preview?.ai_validation?.summary || "Jugada validada por el sistema.";
    if (!canPreview) {
      toast.error("Completa todos los campos requeridos");
      return;
    }
    setSaving(true);
    try {
      await apiClient.post("/admin/picks", {
        sport, league: selectedEvent.league,
        home_team: selectedEvent.home_team, home_logo: selectedEvent.home_logo || "",
        away_team: selectedEvent.away_team, away_logo: selectedEvent.away_logo || "",
        match_date: selectedEvent.match_date, market, pick_selection: pickSelection,
        confidence, units, stake: units,
        analysis: finalAnalysis, stats: buildStatsPayload(),
        access_tier: "premium",
        is_paid_pick: !!(isPaidPick && pickPrice && parseFloat(pickPrice) > 0),
        is_iq_pick: isIqPick,
        pick_price: isPaidPick && pickPrice ? parseFloat(pickPrice) : null,
      });
      toast.success("Señal publicada");
      nav("/admin/analisis");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al publicar");
    } finally {
      setSaving(false);
    }
  };

  const addFactor = () => {
    if (newFactor.trim()) {
      updateStats("key_factors", [...(stats.key_factors || []), newFactor.trim()]);
      setNewFactor("");
    }
  };

  const [isPaidPick, setIsPaidPick] = useState(false);
  const [isIqPick, setIsIqPick] = useState(false);
  const [pickPrice, setPickPrice]   = useState("");

  // Derived step states
  const matchSelected = selectedEvent !== null;
  const marketDone    = matchSelected && marketConfirmed && pickSelection !== "";
  const analyzed      = preview !== null;

  const currentStep = !sportConfirmed ? 1 : !matchSelected ? 2 : !marketDone ? 3 : !analyzed ? 4 : 5;

  return (
    <div className="max-w-[700px] mx-auto px-4 pt-6 pb-20">

      {/* Back */}
      <button
        onClick={() => nav("/admin")}
        className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-foreground mb-5 uppercase tracking-wider transition-colors"
        data-testid="admin-create-back"
      >
        <CaretLeft className="w-3.5 h-3.5" /> Panel admin
      </button>

      {/* Header */}
      <div className="mb-7">
        <div className="text-[11px] text-yellow-400 tracking-[0.25em] uppercase font-bold mb-1.5">Asistente de jugadas</div>
        <h1 className="text-display font-black text-3xl lg:text-4xl tracking-tighter leading-none">Nueva jugada</h1>
        <p className="text-zinc-500 mt-1.5 text-sm">Sigue los pasos para crear y publicar una jugada en segundos.</p>
      </div>

      {/* Step progress bar */}
      <StepProgress currentStep={currentStep} />

      {/* ── PASO 1: Deporte ─────────────────────────────────── */}
      <WizardStep number={1} label="Deporte" done={sportConfirmed} active={!sportConfirmed}>
        <div className="grid grid-cols-3 gap-3">
          {Object.entries(SPORT_DISPLAY).map(([id, { label, iconSize, leagues }]) => {
            const active = sport === id && sportConfirmed;
            const pickSport = (leagueKey) => {
              setSport(id);
              setLeagueFilter(leagueKey || null);
              setSportConfirmed(true);
              setSelectedEvent(null);
              setMarketConfirmed(false);
              resetAnalysis();
            };
            return (
              <button
                key={id}
                type="button"
                onClick={() => pickSport(leagues ? leagueFilter : null)}
                data-testid={`admin-field-sport-${id}`}
                className={`flex flex-col items-center gap-3 px-3 py-5 rounded-xl border transition-all duration-200 ${
                  active
                    ? "bg-yellow-500/10 border-yellow-500/30 text-yellow-400 shadow-[0_0_12px_-4px_rgba(234,179,8,0.3)]"
                    : "bg-white/[0.02] border-white/[0.06] text-zinc-400 hover:border-white/[0.15] hover:text-foreground"
                }`}
              >
                {leagues ? (
                  <span className="flex items-center justify-center gap-1.5" style={{ height: iconSize }}>
                    {leagues.map((lg) => (
                      <span
                        key={lg.key}
                        role="button"
                        tabIndex={0}
                        data-testid={`admin-field-league-${lg.key}`}
                        onClick={(e) => { e.stopPropagation(); pickSport(lg.key); }}
                        title={lg.label}
                        className={`rounded-lg p-1 transition-all ${
                          leagueFilter === lg.key && active
                            ? "bg-yellow-500/20 ring-1 ring-yellow-500/50"
                            : "hover:bg-white/[0.06]"
                        }`}
                      >
                        <LeagueLogo sport={lg.key} size={54} />
                      </span>
                    ))}
                  </span>
                ) : (
                  <LeagueLogo sport={id} size={iconSize} />
                )}
                <span className="text-xs font-bold tracking-wider uppercase">{label}</span>
              </button>
            );
          })}
        </div>
      </WizardStep>

      {/* ── PASO 2: Partido ─────────────────────────────────── */}
      {sportConfirmed && (
        <WizardStep number={2} label="Partido" done={matchSelected} active={sportConfirmed && !matchSelected} className="animate-fade-in-up">
          <MatchSelector
            sport={sport}
            leagueFilter={leagueFilter}
            selected={selectedEvent}
            onSelect={(ev) => {
              setSelectedEvent(ev);
              setMarketConfirmed(false);
              resetAnalysis();
            }}
          />
        </WizardStep>
      )}

      {/* ── PASO 3: Jugada (Mercado + Selección) ──────────────── */}
      {matchSelected && (
        <WizardStep number={3} label="Jugada" done={marketDone} active={matchSelected && !marketDone} className="animate-fade-in-up">
          <div className="space-y-4">
            <div>
              <label className="block text-[10px] text-zinc-500 uppercase tracking-[0.15em] font-bold mb-2.5">Mercado</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {MARKETS_CONFIG[sport].map(m => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => { setMarket(m.id); setMarketConfirmed(true); setSelectionData({}); resetAnalysis(); }}
                    data-testid={`admin-market-${m.id}`}
                    className={`text-left px-4 py-3 rounded-xl border text-sm font-semibold transition-all duration-200 ${
                      market === m.id && marketConfirmed
                        ? "bg-yellow-500/10 border-yellow-500/30 text-yellow-400"
                        : "bg-white/[0.02] border-white/[0.06] text-zinc-300 hover:border-white/[0.15]"
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
            {marketConfirmed && (
              <div>
                <label className="block text-[10px] text-zinc-500 uppercase tracking-[0.15em] font-bold mb-2.5">Selección</label>
                <SelectionBuilder
                  marketConfig={marketConfig}
                  home={selectedEvent.home_team}
                  away={selectedEvent.away_team}
                  value={selectionData}
                  onChange={(d) => { setSelectionData(d); resetAnalysis(); }}
                />
                {pickSelection && (
                  <div className="mt-2.5 flex items-center gap-2 text-xs text-emerald-400">
                    <CheckFat weight="fill" className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="font-semibold" data-testid="admin-pick-preview-text">
                      Jugada: <span className="text-foreground">{pickSelection}</span>
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </WizardStep>
      )}

      {/* ── PASO 4: Configurar Pick ──────────────────────────── */}
      {marketDone && !analyzed && (
        <WizardStep number={4} label="Configurar" done={false} active={true} className="animate-fade-in-up">
          <div className="space-y-5">

            {/* Confianza slider */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-[10px] text-zinc-500 uppercase tracking-[0.15em] font-bold">Confianza del analista</label>
                <span className="number-ticker font-black text-yellow-400 text-lg">{confidence}<span className="text-zinc-400 text-xs font-normal">/10</span></span>
              </div>
              <input type="range" min="1" max="10" step="1" value={confidence}
                onChange={e => setConfidence(parseInt(e.target.value))}
                className="w-full accent-yellow-400" data-testid="admin-field-confidence" />
              <div className="flex justify-between text-[9px] text-zinc-400 mt-1">
                <span>1 · Baja</span><span>5 · Media</span><span>10 · Alta</span>
              </div>
            </div>

            {/* Unidades slider */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-[10px] text-zinc-500 uppercase tracking-[0.15em] font-bold">Unidades recomendadas</label>
                <span className="number-ticker font-black text-emerald-400 text-lg">{units}<span className="text-zinc-400 text-xs font-normal">u</span></span>
              </div>
              <input type="range" min="1" max="10" step="1" value={units}
                onChange={e => setUnits(parseInt(e.target.value))}
                className="w-full accent-emerald-400" data-testid="admin-field-units" />
              <div className="flex justify-between text-[9px] text-zinc-400 mt-1">
                <span>1u · Mínimo</span><span>5u · Estándar</span><span>10u · Máximo</span>
              </div>
            </div>

            {/* Análisis personal */}
            <div>
              <label className="block text-[10px] text-zinc-500 uppercase tracking-[0.15em] font-bold mb-2">
                Tu análisis personal <span className="text-zinc-700 normal-case tracking-normal font-normal">(opcional)</span>
              </label>
              <textarea value={analysis} onChange={e => setAnalysis(e.target.value)} rows={4}
                className="input-field resize-none w-full text-sm"
                placeholder="Escribe tu lectura de la jugada para los suscriptores..."
                data-testid="admin-field-analysis" />
            </div>

            {/* MLB Stats Banner — visible siempre que sea partido MLB */}
            {sport === "mlb" && selectedEvent && (
              <div
                data-testid="mlb-stats-banner"
                className="rounded-xl p-4 border"
                style={{
                  background: "linear-gradient(135deg, rgba(212,175,55,0.10) 0%, rgba(212,175,55,0.04) 100%)",
                  borderColor: "rgba(212,175,55,0.3)",
                }}
              >
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden"
                         style={{ background: "rgba(212,175,55,0.15)", border: "1px solid rgba(212,175,55,0.3)" }}>
                      <SportIcon sport="mlb" size={26} />
                    </div>
                    <div>
                      <div className="text-xs font-black uppercase tracking-widest" style={{ color: "#D4AF37" }}>
                        MLB Stats API · Datos en vivo
                      </div>
                      <div className="text-[11px] mt-0.5" style={{ color: "#9CA3AF" }}>
                        {mlbFetched
                          ? `✅ Pitchers cargados: ${stats.pitcher_home_name || "N/D"} vs ${stats.pitcher_away_name || "N/D"}`
                          : mlbFetching
                            ? "Buscando pitchers probables…"
                            : "Pitchers ERA, WHIP, récord — auto-cargados (gratis, oficial MLB)"}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    data-testid="mlb-banner-fetch-btn"
                    onClick={fetchMlbPitchers}
                    disabled={mlbFetching}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-black uppercase tracking-wider transition-all disabled:opacity-50"
                    style={{
                      background: "rgba(212,175,55,0.18)",
                      color: "#D4AF37",
                      border: "1px solid rgba(212,175,55,0.4)",
                    }}>
                    <ArrowClockwise className={`w-3.5 h-3.5 ${mlbFetching ? "animate-spin" : ""}`} />
                    {mlbFetching ? "Cargando…" : mlbFetched ? "Recargar" : "Cargar pitchers"}
                  </button>
                </div>
                {mlbFetched && (stats.pitcher_home_era || stats.pitcher_away_era) && (
                  <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t" style={{ borderColor: "rgba(212,175,55,0.15)" }}>
                    <div className="rounded-lg p-2.5" style={{ background: "rgba(255,255,255,0.03)" }}>
                      <div className="text-[10px] uppercase tracking-wider font-bold mb-1" style={{ color: "#9CA3AF" }}>
                        Local · {selectedEvent.home_team}
                      </div>
                      <div className="text-sm font-bold text-white truncate">{stats.pitcher_home_name || "—"}</div>
                      <div className="flex gap-3 mt-1 text-[11px]">
                        <span style={{ color: "#D4AF37" }}>ERA <strong className="text-white">{stats.pitcher_home_era || "—"}</strong></span>
                        <span style={{ color: "#D4AF37" }}>WHIP <strong className="text-white">{stats.pitcher_home_whip || "—"}</strong></span>
                        <span style={{ color: "#D4AF37" }}>{stats.pitcher_home_record || "?-?"}</span>
                      </div>
                    </div>
                    <div className="rounded-lg p-2.5" style={{ background: "rgba(255,255,255,0.03)" }}>
                      <div className="text-[10px] uppercase tracking-wider font-bold mb-1" style={{ color: "#9CA3AF" }}>
                        Visitante · {selectedEvent.away_team}
                      </div>
                      <div className="text-sm font-bold text-white truncate">{stats.pitcher_away_name || "—"}</div>
                      <div className="flex gap-3 mt-1 text-[11px]">
                        <span style={{ color: "#D4AF37" }}>ERA <strong className="text-white">{stats.pitcher_away_era || "—"}</strong></span>
                        <span style={{ color: "#D4AF37" }}>WHIP <strong className="text-white">{stats.pitcher_away_whip || "—"}</strong></span>
                        <span style={{ color: "#D4AF37" }}>{stats.pitcher_away_record || "?-?"}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Advanced accordion */}
            <div className="border-t border-white/[0.06] pt-4">
              <button type="button" onClick={() => setShowAdvanced(p => !p)}
                className="flex items-center gap-2 text-xs text-zinc-500 hover:text-zinc-300 transition-colors font-semibold uppercase tracking-[0.15em]"
                data-testid="admin-toggle-advanced">
                {showAdvanced ? <CaretUp className="w-3.5 h-3.5" /> : <CaretDown className="w-3.5 h-3.5" />}
                Datos avanzados del partido
                <span className="text-[10px] font-normal normal-case tracking-normal text-zinc-400">(mejora la evaluación)</span>
                {statsLoading && <ArrowClockwise className="w-3 h-3 animate-spin text-zinc-400 ml-1" />}
                {statsLoaded  && !statsLoading && <CheckCircle weight="fill" className="w-3 h-3 text-emerald-500 ml-1" title="Datos reales cargados" />}
              </button>
              {showAdvanced && (
                <div className="mt-4 grid grid-cols-2 gap-4 animate-fade-in-up">
                  <AdvField label={`Forma local (${selectedEvent.home_team})`}>
                    <input className="input-field uppercase" maxLength={5} value={stats.form_home} onChange={e => updateStats("form_home", e.target.value.toUpperCase())} placeholder="WWLDW" data-testid="admin-stats-form-home" />
                  </AdvField>
                  <AdvField label={`Forma visitante (${selectedEvent.away_team})`}>
                    <input className="input-field uppercase" maxLength={5} value={stats.form_away} onChange={e => updateStats("form_away", e.target.value.toUpperCase())} placeholder="WLLDW" data-testid="admin-stats-form-away" />
                  </AdvField>
                  <AdvField label={`Factor casa: ${stats.home_advantage}%`} full>
                    <input type="range" min="0" max="100" value={stats.home_advantage} onChange={e => updateStats("home_advantage", e.target.value)} className="w-full accent-yellow-400" data-testid="admin-stats-home-adv" />
                  </AdvField>
                  <AdvField label={sport === "football" ? "Goles local/juego" : sport === "nba" ? "Pts local/juego" : "Carreras local/juego"}>
                    <input type="number" step="0.1" className="input-field" value={stats.recent_goals_home} onChange={e => updateStats("recent_goals_home", e.target.value)} placeholder="2.3" />
                  </AdvField>
                  <AdvField label={sport === "football" ? "Goles visitante/juego" : sport === "nba" ? "Pts visitante/juego" : "Carreras visitante/juego"}>
                    <input type="number" step="0.1" className="input-field" value={stats.recent_goals_away} onChange={e => updateStats("recent_goals_away", e.target.value)} placeholder="1.8" />
                  </AdvField>
                  <AdvField label="Head to Head" full>
                    <input className="input-field" value={stats.h2h_summary} onChange={e => updateStats("h2h_summary", e.target.value)} placeholder="Últimos 5: 4W 1D" />
                  </AdvField>

                  {/* ── MLB Pitcher Section ── */}
                  {sport === "mlb" && (
                    <div className="col-span-2 space-y-3" data-testid="mlb-pitcher-section">
                      {/* Header + auto-fetch button */}
                      <div className="flex items-center justify-between pt-2 border-t border-white/[0.06]">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-yellow-400 uppercase tracking-widest">Pitchers Abridores</span>
                          {mlbFetched && <span className="text-[10px] text-emerald-400 flex items-center gap-1"><CheckCircle weight="fill" className="w-3 h-3" /> MLB Stats API</span>}
                        </div>
                        <button
                          type="button"
                          data-testid="fetch-mlb-pitchers-btn"
                          onClick={fetchMlbPitchers}
                          disabled={mlbFetching || !selectedEvent}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-yellow-500/10 border border-yellow-500/20 text-yellow-400 hover:bg-yellow-500/20 transition-all text-xs font-semibold disabled:opacity-40"
                        >
                          <ArrowClockwise className={`w-3 h-3 ${mlbFetching ? "animate-spin" : ""}`} />
                          {mlbFetching ? "Buscando..." : "Auto-fetch MLB"}
                        </button>
                      </div>

                      {/* Home pitcher */}
                      <div className="rounded-xl border border-white/[0.06] p-3 bg-white/[0.02] space-y-2">
                        <div className="text-[10px] text-zinc-500 font-semibold uppercase tracking-widest mb-2">
                          Abridor Local — {selectedEvent?.home_team || "Local"}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <AdvField label="Nombre del abridor" full>
                            <input className="input-field" value={stats.pitcher_home_name} onChange={e => updateStats("pitcher_home_name", e.target.value)} placeholder="Nombre pitcher" data-testid="pitcher-home-name" />
                          </AdvField>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <AdvField label="ERA">
                            <input type="number" step="0.01" className="input-field" value={stats.pitcher_home_era} onChange={e => updateStats("pitcher_home_era", e.target.value)} placeholder="3.45" data-testid="pitcher-home-era" />
                          </AdvField>
                          <AdvField label="WHIP">
                            <input type="number" step="0.01" className="input-field" value={stats.pitcher_home_whip} onChange={e => updateStats("pitcher_home_whip", e.target.value)} placeholder="1.12" data-testid="pitcher-home-whip" />
                          </AdvField>
                          <AdvField label="Récord (T-D)">
                            <input className="input-field" value={stats.pitcher_home_record} onChange={e => updateStats("pitcher_home_record", e.target.value)} placeholder="3-2" data-testid="pitcher-home-record" />
                          </AdvField>
                        </div>
                        <AdvField label="ERA F5 (primeras 5 entradas)">
                          <input type="number" step="0.01" className="input-field" value={stats.pitcher_home_era_f5} onChange={e => updateStats("pitcher_home_era_f5", e.target.value)} placeholder="2.80" data-testid="pitcher-home-era-f5" />
                        </AdvField>
                      </div>

                      {/* Away pitcher */}
                      <div className="rounded-xl border border-white/[0.06] p-3 bg-white/[0.02] space-y-2">
                        <div className="text-[10px] text-zinc-500 font-semibold uppercase tracking-widest mb-2">
                          Abridor Visitante — {selectedEvent?.away_team || "Visitante"}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <AdvField label="Nombre del abridor" full>
                            <input className="input-field" value={stats.pitcher_away_name} onChange={e => updateStats("pitcher_away_name", e.target.value)} placeholder="Nombre pitcher" data-testid="pitcher-away-name" />
                          </AdvField>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <AdvField label="ERA">
                            <input type="number" step="0.01" className="input-field" value={stats.pitcher_away_era} onChange={e => updateStats("pitcher_away_era", e.target.value)} placeholder="4.20" data-testid="pitcher-away-era" />
                          </AdvField>
                          <AdvField label="WHIP">
                            <input type="number" step="0.01" className="input-field" value={stats.pitcher_away_whip} onChange={e => updateStats("pitcher_away_whip", e.target.value)} placeholder="1.35" data-testid="pitcher-away-whip" />
                          </AdvField>
                          <AdvField label="Récord (T-D)">
                            <input className="input-field" value={stats.pitcher_away_record} onChange={e => updateStats("pitcher_away_record", e.target.value)} placeholder="2-4" data-testid="pitcher-away-record" />
                          </AdvField>
                        </div>
                        <AdvField label="ERA F5 (primeras 5 entradas)">
                          <input type="number" step="0.01" className="input-field" value={stats.pitcher_away_era_f5} onChange={e => updateStats("pitcher_away_era_f5", e.target.value)} placeholder="3.60" data-testid="pitcher-away-era-f5" />
                        </AdvField>
                      </div>

                      {/* H2H last 5 */}
                      <AdvField label="Head to Head — últimos 5 (victorias local-visitante)" full>
                        <input className="input-field" value={stats.h2h_last5} onChange={e => updateStats("h2h_last5", e.target.value)} placeholder="3-2" data-testid="h2h-last5" />
                      </AdvField>
                    </div>
                  )}
                  <AdvField label="Bajas / Lesiones" full>
                    <input className="input-field" value={stats.injuries} onChange={e => updateStats("injuries", e.target.value)} placeholder="Sin bajas importantes" />
                  </AdvField>
                  <AdvField label="Factores clave" full>
                    <div className="flex gap-2 mb-2">
                      <input className="input-field flex-1" value={newFactor} onChange={e => setNewFactor(e.target.value)} onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addFactor())} placeholder="Agregar factor..." data-testid="admin-stats-factor-input" />
                      <button type="button" onClick={addFactor} className="btn-secondary text-sm px-3" data-testid="admin-stats-factor-add">Agregar</button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {(stats.key_factors || []).map((f, i) => (
                        <span key={i} className="chip-muted group">
                          {f}
                          <button type="button" onClick={() => updateStats("key_factors", stats.key_factors.filter((_, j) => j !== i))} className="opacity-60 hover:opacity-100 ml-1">×</button>
                        </span>
                      ))}
                    </div>
                  </AdvField>
                  <AdvField label="Factores contextuales" full>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                      {CONTEXTUAL_FACTORS.map(f => (
                        <label key={f.id} className="flex items-center gap-2.5 cursor-pointer group py-1.5 px-2 rounded-lg hover:bg-white/[0.03] transition-colors">
                          <input type="checkbox"
                            checked={(stats.contextual_factors || []).includes(f.id)}
                            onChange={e => {
                              const cur = stats.contextual_factors || [];
                              updateStats("contextual_factors", e.target.checked ? [...cur, f.id] : cur.filter(id => id !== f.id));
                            }}
                            className="w-3.5 h-3.5 rounded flex-shrink-0 accent-yellow-400"
                            data-testid={`admin-ctx-factor-${f.id}`} />
                          <span className="text-xs text-zinc-400 group-hover:text-zinc-300 transition-colors leading-snug">{f.label}</span>
                        </label>
                      ))}
                    </div>
                  </AdvField>
                </div>
              )}
            </div>

            {/* MONTE CARLO PANEL */}
            {(mcSim || mcLoading) && (
              <MonteCarloPanel
                mcSim={mcSim}
                mcLoading={mcLoading}
                homeTeam={selectedEvent?.home_team}
                awayTeam={selectedEvent?.away_team}
                onApplySuggestion={(suggestedConf) => setConfidence(suggestedConf)}
              />
            )}

            {/* ANALIZAR SEÑAL button */}
            <div className="pt-1">
              <button onClick={runPreview} disabled={!canPreview || loadingPreview}
                className="w-full rounded-2xl font-black text-xl py-6 flex items-center justify-center gap-3 tracking-tight transition-all duration-200 disabled:cursor-not-allowed
                  enabled:bg-emerald-500 enabled:hover:bg-emerald-400 enabled:text-white enabled:shadow-[0_4px_28px_-6px_rgba(16,185,129,0.50)] enabled:hover:shadow-[0_8px_40px_-6px_rgba(16,185,129,0.65)] enabled:hover:scale-[1.01] enabled:active:scale-[0.99]
                  disabled:bg-white/[0.04] disabled:text-zinc-700 disabled:border disabled:border-white/[0.06]"
                data-testid="admin-run-preview">
                {loadingPreview
                  ? <><ArrowClockwise className="w-6 h-6 animate-spin" /> Analizando...</>
                  : <><Sparkle weight="fill" className="w-6 h-6" /> ANALIZAR SEÑAL</>
                }
              </button>
            </div>
          </div>
        </WizardStep>
      )}

      {/* ── PASO 5: Análisis del sistema + Publicar ─────────────── */}
      {analyzed && (
        <div className="space-y-5 mt-2 animate-fade-in-up">

          {/* Header */}
          <div className="flex items-center gap-3">
            <CheckFat weight="fill" className="w-4 h-4 text-emerald-400 flex-shrink-0" />
            <span className="text-[11px] text-zinc-400 uppercase tracking-[0.2em] font-bold">
              Análisis completado
            </span>
            <div className="flex-1 h-px bg-white/[0.06]" />
            <button
              onClick={runPreview}
              disabled={loadingPreview}
              className="text-[10px] text-zinc-400 hover:text-zinc-400 flex items-center gap-1 transition-colors"
              data-testid="admin-reanalyze"
            >
              <ArrowClockwise className={`w-3 h-3 ${loadingPreview ? "animate-spin" : ""}`} />
              {loadingPreview ? "Analizando..." : "Re-analizar"}
            </button>
          </div>

          {/* 1. Veredicto banner */}
          <VeredictoBanner preview={preview} />

          {/* 3. Fortalezas + Advertencias */}
          {(preview.ai_validation?.pros?.length > 0 || preview.ai_validation?.cons?.length > 0) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {preview.ai_validation?.pros?.length > 0 && (
                <FortalezasCard pros={preview.ai_validation.pros} />
              )}
              {preview.ai_validation?.cons?.length > 0 && (
                <AdvertenciasCard cons={preview.ai_validation.cons} />
              )}
            </div>
          )}

          {/* 4. Risk warning */}
          {preview.ai_validation?.risk_warning && (
            <div className="flex items-start gap-3 px-4 py-3.5 bg-yellow-500/5 border border-yellow-500/20 rounded-xl">
              <Warning weight="fill" className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-yellow-200 leading-relaxed">{preview.ai_validation.risk_warning}</p>
            </div>
          )}

          {/* 5. Resumen ejecutivo */}
          {preview.ai_validation?.summary && (
            <ResumenEjecutivo summary={preview.ai_validation.summary} />
          )}

          {/* 6. Notas para suscriptores — editable antes de publicar */}
          <div className="surface rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="w-4 h-4" style={{ color: "#D4AF37" }} weight="fill" />
              <div className="text-[10px] text-zinc-400 uppercase tracking-[0.2em] font-bold">
                Notas para suscriptores
              </div>
              <span className="text-[10px] text-zinc-400 normal-case tracking-normal font-normal">(visible en la señal)</span>
            </div>
            <textarea
              value={analysis}
              onChange={e => setAnalysis(e.target.value)}
              rows={4}
              className="input-field resize-none w-full text-sm"
              placeholder="Escribe tus indicaciones para los suscriptores... Ej: 'Apuesten sobre 2.5 goles, el equipo local tiene ventaja en casa y el rival viene con lesiones clave.'"
              data-testid="admin-field-analysis-publish"
            />
            {analysis && (
              <p className="text-[10px] text-emerald-400 mt-1.5 flex items-center gap-1">
                <CheckCircle weight="fill" className="w-3 h-3" /> Este texto aparecerá en la señal publicada
              </p>
            )}
            {!analysis && (
              <p className="text-[10px] text-zinc-400 mt-1.5">
                Si no escribes nada, se usará el resumen ejecutivo del sistema.
              </p>
            )}
          </div>

          {/* 7. Pick VIP — venta individual ────────────────── */}
          <div
            className="rounded-2xl p-4 border"
            style={{
              borderColor: isPaidPick ? "rgba(212,175,55,0.4)" : "rgba(255,255,255,0.06)",
              background: isPaidPick
                ? "linear-gradient(135deg, rgba(212,175,55,0.08) 0%, rgba(212,175,55,0.02) 100%)"
                : "rgba(255,255,255,0.02)",
            }}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-base">💎</span>
                  <span className="text-sm font-black uppercase tracking-wider" style={{ color: isPaidPick ? "#D4AF37" : "#9CA3AF" }}>
                    Pick VIP — venta individual
                  </span>
                </div>
                <p className="text-xs" style={{ color: "#9CA3AF" }}>
                  Si activas esto, este pick se vende <strong>aparte</strong> a un precio fijo. Los usuarios (free, premium o vip) lo verán bloqueado y deberán pagar para verlo.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={isPaidPick}
                onClick={() => setIsPaidPick(v => !v)}
                data-testid="admin-toggle-paid-pick"
                className="flex-shrink-0 w-12 h-7 rounded-full transition-all relative"
                style={{
                  background: isPaidPick ? "rgba(212,175,55,0.4)" : "rgba(255,255,255,0.08)",
                  border: `1px solid ${isPaidPick ? "rgba(212,175,55,0.6)" : "rgba(255,255,255,0.12)"}`,
                }}
              >
                <span
                  className="absolute top-0.5 w-5 h-5 rounded-full transition-all"
                  style={{
                    background: isPaidPick ? "#D4AF37" : "#71717A",
                    left: isPaidPick ? "26px" : "4px",
                  }}
                />
              </button>
            </div>
            {isPaidPick && (
              <div className="mt-3 flex items-center gap-2">
                <span className="text-xl font-black" style={{ color: "#D4AF37" }}>$</span>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={pickPrice}
                  onChange={e => setPickPrice(e.target.value)}
                  placeholder="30"
                  data-testid="admin-pick-price-input"
                  className="flex-1 bg-transparent text-2xl font-black text-white outline-none border-b border-yellow-500/30 focus:border-yellow-500 py-1"
                />
                <span className="text-xs uppercase tracking-wider" style={{ color: "#9CA3AF" }}>USD</span>
              </div>
            )}
          </div>

          {/* 7b. IQ del Día ────────────────── */}
          <div
            className="rounded-2xl p-4 border"
            style={{
              borderColor: isIqPick ? "rgba(212,175,55,0.4)" : "rgba(255,255,255,0.06)",
              background: isIqPick
                ? "linear-gradient(135deg, rgba(212,175,55,0.08) 0%, rgba(212,175,55,0.02) 100%)"
                : "rgba(255,255,255,0.02)",
            }}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-base">🧠</span>
                  <span className="text-sm font-black uppercase tracking-wider" style={{ color: isIqPick ? "#D4AF37" : "#9CA3AF" }}>
                    IQ del Día
                  </span>
                </div>
                <p className="text-xs" style={{ color: "#9CA3AF" }}>
                  Marca esta señal como la estrella del día: aparece destacada en el dashboard
                  y se envía con notificación push especial a todos los usuarios.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={isIqPick}
                onClick={() => setIsIqPick(v => !v)}
                data-testid="admin-toggle-iq-pick"
                className="flex-shrink-0 w-12 h-7 rounded-full transition-all relative"
                style={{
                  background: isIqPick ? "rgba(212,175,55,0.4)" : "rgba(255,255,255,0.08)",
                  border: `1px solid ${isIqPick ? "rgba(212,175,55,0.6)" : "rgba(255,255,255,0.12)"}`,
                }}
              >
                <span
                  className="absolute top-0.5 w-5 h-5 rounded-full transition-all"
                  style={{
                    background: isIqPick ? "#D4AF37" : "#71717A",
                    left: isIqPick ? "26px" : "4px",
                  }}
                />
              </button>
            </div>
          </div>

          {/* 8. Publish CTA */}
          <button
            onClick={onSave}
            disabled={saving}
            className="w-full rounded-2xl bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-white font-black text-xl py-7 flex items-center justify-center gap-3 tracking-tight transition-all duration-200 shadow-[0_4px_24px_-6px_rgba(16,185,129,0.4)] hover:shadow-[0_8px_40px_-6px_rgba(16,185,129,0.55)] hover:scale-[1.01] active:scale-[0.99]"
            data-testid="admin-save-pick"
          >
            {saving ? (
              <><ArrowClockwise className="w-6 h-6 animate-spin" /> Publicando...</>
            ) : (
              <>PUBLICAR SEÑAL &rarr;</>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

/* ─── MonteCarloPanel ────────────────────────────────────────── */

function McProbBar({ label, prob, color, highlight }) {
  return (
    <div>
      <div className="flex justify-between items-center mb-1">
        <span className="text-xs truncate font-semibold" style={{ color: "#A1A1AA", maxWidth: "72%" }}>{label}</span>
        <span className="text-xs font-black tabular-nums" style={{ color: highlight ? color : "#555" }}>{prob}%</span>
      </div>
      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.05)" }}>
        <div className="h-full rounded-full transition-all duration-700"
          style={{ width: `${Math.min(prob, 100)}%`, background: highlight ? color : "rgba(255,255,255,0.1)" }} />
      </div>
    </div>
  );
}

function MonteCarloPanel({ mcSim, mcLoading, homeTeam, awayTeam, onApplySuggestion }) {
  const suggestedConf = mcSim?.auto_iq_score != null
    ? Math.min(10, Math.max(1, Math.round(mcSim.auto_iq_score)))
    : null;

  return (
    <div className="rounded-2xl border overflow-hidden" data-testid="admin-mc-panel"
      style={{ background: "rgba(212,175,55,0.03)", borderColor: "rgba(212,175,55,0.18)" }}>

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3"
        style={{ borderBottom: "1px solid rgba(212,175,55,0.1)", background: "rgba(212,175,55,0.04)" }}>
        <div className="flex items-center gap-2">
          <ChartLine weight="fill" className="w-4 h-4" style={{ color: "#D4AF37" }} />
          <span className="text-[11px] font-black tracking-[0.2em] uppercase" style={{ color: "#D4AF37" }}>
            Simulación Monte Carlo
          </span>
        </div>
        {mcLoading ? (
          <div className="flex items-center gap-1.5">
            <ArrowClockwise className="w-3 h-3 animate-spin" style={{ color: "#555" }} />
            <span className="text-[10px] font-bold" style={{ color: "#555" }}>Calculando 50,000 escenarios...</span>
          </div>
        ) : mcSim ? (
          <span className="text-[9px] font-bold px-2 py-0.5 rounded-full"
            style={{ background: "rgba(0,0,0,0.3)", color: "#666", border: "1px solid rgba(255,255,255,0.07)" }}>
            {(mcSim.simulations || 50000).toLocaleString()} sims
          </span>
        ) : null}
      </div>

      <div className="px-4 py-4">
        {/* Skeleton cargando */}
        {mcLoading && !mcSim && (
          <div className="space-y-3 animate-pulse">
            {[100, 75, 55].map((w, i) => (
              <div key={i}>
                <div className="flex justify-between mb-1">
                  <div className="h-3 rounded" style={{ background: "rgba(255,255,255,0.06)", width: `${w * 0.45}%` }} />
                  <div className="h-3 w-10 rounded" style={{ background: "rgba(255,255,255,0.06)" }} />
                </div>
                <div className="h-1.5 rounded-full" style={{ background: "rgba(255,255,255,0.06)" }} />
              </div>
            ))}
          </div>
        )}

        {/* Resultados */}
        {mcSim && (
          <>
            <div className="space-y-3 mb-4">
              <McProbBar
                label={homeTeam || "Local"}
                prob={mcSim.home_win_prob}
                color="#10B981"
                highlight={mcSim.home_win_prob >= mcSim.away_win_prob && mcSim.home_win_prob >= (mcSim.draw_prob || 0)}
              />
              {mcSim.draw_prob > 0 && (
                <McProbBar label="Empate" prob={mcSim.draw_prob} color="#71717A" highlight={false} />
              )}
              <McProbBar
                label={awayTeam || "Visitante"}
                prob={mcSim.away_win_prob}
                color="#D4AF37"
                highlight={mcSim.away_win_prob > mcSim.home_win_prob && mcSim.away_win_prob > (mcSim.draw_prob || 0)}
              />
            </div>

            {/* Métricas extra */}
            <div className="grid grid-cols-2 gap-2 mb-4 text-[11px]"
              style={{ borderTop: "1px solid rgba(255,255,255,0.05)", paddingTop: 12 }}>
              {mcSim.most_likely_score && (
                <div className="flex flex-col gap-0.5 col-span-2">
                  <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: "#52525B" }}>Scores más probables</span>
                  {/* Top 3 scores si disponibles */}
                  {mcSim.top_scores && mcSim.top_scores.length > 0 ? (
                    <div className="flex gap-2 flex-wrap mt-0.5">
                      {mcSim.top_scores.map((s, i) => (
                        <div key={i} className="flex items-center gap-1.5 px-2 py-1 rounded-lg"
                          style={{
                            background: i === 0 ? "rgba(212,175,55,0.12)" : "rgba(255,255,255,0.04)",
                            border: i === 0 ? "1px solid rgba(212,175,55,0.3)" : "1px solid rgba(255,255,255,0.06)"
                          }}>
                          <span className="font-mono font-black text-sm" style={{ color: i === 0 ? "#D4AF37" : "#a1a1aa" }}>{s.score}</span>
                          <span className="text-[10px] font-semibold" style={{ color: i === 0 ? "rgba(212,175,55,0.7)" : "#52525b" }}>{s.probability}%</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="font-mono font-black text-base text-white">{mcSim.most_likely_score}</span>
                  )}
                </div>
              )}
              {mcSim.auto_iq_score != null && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: "#52525B" }}>IQ Sugerido</span>
                  <span className="font-black text-base" style={{ color: "#D4AF37" }}>{mcSim.auto_iq_score} / 10</span>
                </div>
              )}
              {mcSim.edge_home != null && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: "#52525B" }}>Edge vs mercado</span>
                  <span className={`font-black text-sm ${mcSim.edge_home > 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {mcSim.edge_home > 0 ? "+" : ""}{mcSim.edge_home}%
                  </span>
                </div>
              )}
              {mcSim.model && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: "#52525B" }}>Modelo</span>
                  <span className="text-zinc-400 text-[11px] font-semibold">{mcSim.model}</span>
                </div>
              )}
            </div>

            {/* Botón aplicar sugerencia */}
            {suggestedConf != null && (
              <button
                type="button"
                onClick={() => onApplySuggestion(suggestedConf)}
                data-testid="admin-mc-apply-suggestion"
                className="w-full py-2.5 rounded-xl text-xs font-black tracking-wider uppercase transition-all duration-200 flex items-center justify-center gap-2"
                style={{
                  background: "rgba(212,175,55,0.1)",
                  border: "1px solid rgba(212,175,55,0.3)",
                  color: "#D4AF37",
                }}
              >
                <Robot weight="fill" className="w-3.5 h-3.5" />
                Aplicar confianza sugerida ({suggestedConf}/10)
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* ─── Wizard UI Components ──────────────────────────────────── */

function StepProgress({ currentStep }) {
  const steps = [
    { n: 1, label: "Deporte" },
    { n: 2, label: "Partido" },
    { n: 3, label: "Jugada" },
    { n: 4, label: "Configurar" },
    { n: 5, label: "Publicar" },
  ];
  return (
    <div className="flex items-center mb-8" data-testid="admin-step-progress">
      {steps.map((s, i) => {
        const done   = currentStep > s.n;
        const active = currentStep === s.n;
        return (
          <Fragment key={s.n}>
            <div className="flex flex-col items-center gap-1 flex-shrink-0">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-black transition-all duration-300 ${
                done   ? "bg-emerald-400 text-black"
                : active ? "bg-yellow-400 text-black shadow-[0_0_12px_-2px_rgba(234,179,8,0.6)]"
                : "bg-white/[0.06] text-zinc-500"
              }`}>
                {done ? <CheckFat weight="bold" className="w-3.5 h-3.5" /> : s.n}
              </div>
              <span className={`text-[9px] uppercase tracking-wider font-bold ${
                done ? "text-emerald-400" : active ? "text-yellow-400" : "text-zinc-400"
              }`}>{s.label}</span>
            </div>
            {i < steps.length - 1 && (
              <div className={`flex-1 h-px mx-2 mb-4 transition-all duration-500 ${done ? "bg-emerald-400/40" : "bg-white/[0.06]"}`} />
            )}
          </Fragment>
        );
      })}
    </div>
  );
}

function WizardStep({ number, label, done, active, children, className = "" }) {
  const accentBorder = done
    ? "border-l-[3px] border-l-emerald-400/60"
    : active
      ? "border-l-[3px] border-l-yellow-400/80"
      : "border-l-[3px] border-l-transparent";

  return (
    <div className={`relative surface rounded-2xl mb-4 ${accentBorder} ${className}`}>
      <div className="pl-5 pr-5 pt-5 pb-5">
        {/* Step label */}
        <div className="flex items-center gap-2.5 mb-5">
          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black transition-all ${
            done   ? "bg-emerald-400 text-black"
            : active ? "bg-yellow-400 text-black"
            : "bg-white/[0.08] text-zinc-500"
          }`}>
            {done ? <CheckFat weight="bold" className="w-3 h-3" /> : number}
          </div>
          <span className={`text-xs uppercase tracking-[0.2em] font-bold ${
            done ? "text-emerald-400" : active ? "text-yellow-400" : "text-zinc-500"
          }`}>
            {label}
          </span>
        </div>
        {children}
      </div>
    </div>
  );
}

function AdvField({ label, children, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <label className="block text-[10px] text-zinc-500 uppercase tracking-[0.15em] font-bold mb-1.5">{label}</label>
      {children}
    </div>
  );
}

/* ─── Step 4 — Analysis Components ─────────────────────────── */

function VeredictoBanner({ preview }) {
  const { scoring, ai_validation: ai } = preview;
  const cfgMap = {
    buen_pick: {
      label: "JUGADA RECOMENDADA",
      Icon: CheckCircle,
      gradient: "from-emerald-950/80 to-emerald-900/20",
      border: "border-emerald-500/40",
      color: "text-emerald-400",
      bar: "bg-emerald-400",
      glow: "shadow-[0_0_48px_-12px_rgba(16,185,129,0.35)]",
    },
    dudoso: {
      label: "JUGADA DUDOSA",
      Icon: Warning,
      gradient: "from-yellow-950/80 to-yellow-900/20",
      border: "border-yellow-500/40",
      color: "text-yellow-400",
      bar: "bg-yellow-400",
      glow: "shadow-[0_0_48px_-12px_rgba(234,179,8,0.35)]",
    },
  };
  const cfg = cfgMap[ai?.label] || {
    label: "NO RECOMENDABLE",
    Icon: XCircle,
    gradient: "from-red-950/80 to-red-900/20",
    border: "border-red-500/40",
    color: "text-red-400",
    bar: "bg-red-400",
    glow: "shadow-[0_0_48px_-12px_rgba(239,68,68,0.35)]",
  };

  return (
    <div className={`relative surface rounded-2xl border ${cfg.border} overflow-hidden ${cfg.glow}`} data-testid="admin-result-card">
      <div className={`absolute inset-0 bg-gradient-to-br ${cfg.gradient} pointer-events-none`} />
      <div className="relative px-6 pt-7 pb-6">
        <div className="text-[9px] text-zinc-400 uppercase tracking-[0.25em] font-bold mb-5">
          Veredicto del Sistema
        </div>
        <div className="flex items-center gap-5 mb-7">
          <cfg.Icon weight="fill" className={`w-16 h-16 flex-shrink-0 ${cfg.color}`} />
          <div>
            <div className={`font-black text-2xl sm:text-3xl tracking-tight leading-none ${cfg.color}`}>
              {cfg.label}
            </div>
            <div className="text-sm text-zinc-400 mt-2">
              Clasificación: <span className="font-bold text-foreground">{LABEL_MAP[scoring.label] || scoring.label}</span>
              <span className="mx-2 text-zinc-700">·</span>
              Riesgo: <span className={`font-bold ${{low:"text-emerald-400",medium:"text-yellow-400",high:"text-red-400"}[scoring.risk]}`}>
                {{low:"BAJO",medium:"MEDIO",high:"ALTO"}[scoring.risk]}
              </span>
            </div>
          </div>
        </div>
        <div>
          <div className="flex justify-between items-center mb-2">
            <span className="text-xs text-zinc-500 font-semibold uppercase tracking-wider">Confianza del sistema</span>
            <span className="font-black text-xl number-ticker">
              {scoring.confidence}<span className="text-zinc-400 text-xs font-normal ml-0.5">/100</span>
            </span>
          </div>
          <div className="h-3 bg-black/20 rounded-full overflow-hidden">
            <div
              className={`h-full ${cfg.bar} rounded-full transition-all duration-1000 ease-out`}
              style={{ width: `${scoring.confidence}%` }}
            />
          </div>
          {scoring.confidence < 25 && (
            <p className="text-[10px] text-zinc-400 mt-2 flex items-center gap-1.5">
              <Info className="w-3 h-3 flex-shrink-0" />
              Añade datos avanzados (forma, H2H, stats) para mejorar la confianza
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function FortalezasCard({ pros }) {
  return (
    <div className="surface rounded-2xl p-5 border-l-[3px] border-l-emerald-400/60">
      <div className="flex items-center gap-2 text-[10px] text-emerald-400 uppercase tracking-[0.2em] font-bold mb-4">
        <CheckCircle weight="fill" className="w-3.5 h-3.5" />
        Fortalezas detectadas
      </div>
      <ul className="space-y-3">
        {pros.map((p, i) => (
          <li key={i} className="flex gap-2.5 text-sm text-zinc-300 leading-snug">
            <CheckFat weight="bold" className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 mt-0.5" />
            <span>{p}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function AdvertenciasCard({ cons }) {
  return (
    <div className="surface rounded-2xl p-5 border-l-[3px] border-l-yellow-400/60">
      <div className="flex items-center gap-2 text-[10px] text-yellow-400 uppercase tracking-[0.2em] font-bold mb-4">
        <Warning weight="fill" className="w-3.5 h-3.5" />
        Advertencias
      </div>
      <ul className="space-y-3">
        {cons.map((c, i) => (
          <li key={i} className="flex gap-2.5 text-sm text-zinc-300 leading-snug">
            <Warning weight="fill" className="w-3.5 h-3.5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <span>{c}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ResumenEjecutivo({ summary }) {
  return (
    <div className="surface rounded-2xl p-5">
      <div className="flex items-center gap-2 text-[10px] text-zinc-500 uppercase tracking-[0.2em] font-bold mb-3">
        <FileText className="w-3.5 h-3.5" />
        Resumen ejecutivo
      </div>
      <p className="text-sm text-zinc-300 leading-relaxed" data-testid="admin-preview-ai-summary">
        {summary}
      </p>
    </div>
  );
}

/* ─── MatchSelector ─────────────────────────────────────────── */

function MatchSelector({ sport, leagueFilter, selected, onSelect }) {
  // Etiquetas de liga que devuelve el backend para filtrar
  const LEAGUE_LABELS = { laliga: "LaLiga", ucl: "Champions League", premier: "Premier League" };
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);
  const prevSportRef = useRef(sport);

  // Fetch events when sport or query changes
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        let list = [];
        if (query.trim()) {
          const res = await apiClient.get("/events/search", { params: { sport, q: query } });
          list = res.data.events || [];
        } else {
          const res = await apiClient.get("/events/upcoming", { params: { sport, limit: 20 } });
          list = res.data[sport] || [];
        }
        // Filtrar por liga (LaLiga / Champions / Premier)
        if (leagueFilter && LEAGUE_LABELS[leagueFilter]) {
          const want = LEAGUE_LABELS[leagueFilter].toLowerCase();
          const filtered = list.filter((e) => String(e.league || "").toLowerCase().includes(want.split(" ")[0].toLowerCase()) || String(e.league || "").toLowerCase() === want);
          if (filtered.length) list = filtered;
        }
        if (!cancelled) setEvents(list);
      } catch (err) {
        console.error("[MatchSelector] events fetch failed:", err?.message);
        if (!cancelled) setEvents([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    const timer = setTimeout(load, query ? 300 : 0);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [sport, leagueFilter, query]);

  // Auto-open dropdown when sport changes (so events show immediately)
  useEffect(() => {
    if (prevSportRef.current !== sport) {
      setQuery("");
      setEvents([]);
      setOpen(true);
      prevSportRef.current = sport;
    }
  }, [sport]);

  // Close dropdown on outside click (but not on sport buttons)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    function onClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const SPORT_LABELS = { football: "Fútbol", nba: "NBA", mlb: "MLB", nfl: "NFL", nhl: "NHL" };

  const SIcon = (s) => <SportIcon sport={sport} size={s} />;

  return (
    <div ref={ref} className="relative">
      {selected ? (
        <div className="surface-elevated rounded-xl p-4 flex items-center justify-between gap-3" data-testid="admin-match-selected">
          <div className="flex items-center gap-3 min-w-0">
            {/* Team logos side by side */}
            <div className="flex items-center flex-shrink-0">
              <TeamLogo logoUrl={selected.home_logo} teamName={selected.home_team} size={36} rounded="md" />
              <TeamLogo logoUrl={selected.away_logo} teamName={selected.away_team} size={36} rounded="md" className="-ml-2" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] text-zinc-500 uppercase tracking-wider font-bold">{selected.league}</div>
              <div className="text-sm font-bold truncate">{selected.home_team} vs {selected.away_team}</div>
              <div className="text-xs text-zinc-500 font-mono mt-0.5">
                {selected.match_date && format(new Date(selected.match_date), "EEE dd MMM · HH:mm", { locale: es })}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onSelect(null)}
            data-testid="admin-match-clear"
            className="btn-ghost text-xs flex-shrink-0"
          >
            <X className="w-3.5 h-3.5" /> Cambiar
          </button>
        </div>
      ) : (
        <>
          <div className="relative">
            <MagnifyingGlass className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              type="text"
              value={query}
              onChange={e => { setQuery(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              placeholder={`Buscar partido de ${SPORT_LABELS[sport] || sport}...`}
              className="input-field pl-10 pr-9"
              data-testid="admin-match-search"
            />
            <CaretDown className={`absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`} />
          </div>

          {open && (
            <div className="absolute z-30 left-0 right-0 mt-2 surface-elevated rounded-xl shadow-xl max-h-72 overflow-y-auto scrollbar-thin border border-white/[0.06]">
              {loading ? (
                <div className="p-6 text-center">
                  <div className="flex items-center justify-center gap-2 text-xs text-zinc-500">
                    <ArrowClockwise className="w-4 h-4 animate-spin" />
                    Cargando partidos de {SPORT_LABELS[sport]}...
                  </div>
                </div>
              ) : events.length === 0 ? (
                <div className="py-10 px-6 flex flex-col items-center gap-3 text-center" data-testid="admin-match-empty">
                  <div className="w-12 h-12 rounded-full bg-white/[0.04] border border-white/[0.06] flex items-center justify-center">
                    {SIcon(22)}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-zinc-300">
                      No hay partidos disponibles hoy
                    </div>
                    <div className="text-xs text-zinc-500 mt-1 max-w-[220px]">
                      No encontramos eventos de {SPORT_LABELS[sport]} programados en este momento.
                      {query && <span className="block mt-1">Intenta con otro término de búsqueda.</span>}
                    </div>
                  </div>
                  {query && (
                    <button
                      type="button"
                      onClick={() => setQuery("")}
                      className="text-xs text-yellow-400 hover:text-yellow-300 transition-colors"
                      data-testid="admin-match-clear-search"
                    >
                      Ver todos los partidos →
                    </button>
                  )}
                </div>
              ) : (
                <div className="py-1">
                  {/* Event count header */}
                  <div className="px-4 py-2 border-b border-white/[0.04] flex items-center justify-between">
                    <span className="text-[10px] text-zinc-500 uppercase tracking-wider font-bold">
                      {SPORT_LABELS[sport]}
                    </span>
                    <span className="text-[10px] text-zinc-400">
                      {events.length} partido{events.length !== 1 ? "s" : ""}
                    </span>
                  </div>
                  {events.map(ev => (
                    <button
                      key={ev.id}
                      type="button"
                      onClick={() => { onSelect(ev); setOpen(false); setQuery(""); }}
                      data-testid={`admin-match-option-${ev.id}`}
                      className="w-full text-left px-4 py-3 hover:bg-white/[0.04] transition-colors flex items-center gap-3 border-b border-white/[0.03] last:border-0"
                    >
                      {/* Team logos stacked */}
                      <div className="flex items-center flex-shrink-0">
                        <TeamLogo logoUrl={ev.home_logo} teamName={ev.home_team} size={28} rounded="md" />
                        <TeamLogo logoUrl={ev.away_logo} teamName={ev.away_team} size={28} rounded="md" className="-ml-1.5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold truncate">{ev.home_team} vs {ev.away_team}</div>
                        <div className="text-[11px] text-zinc-500 uppercase tracking-wider mt-0.5 flex items-center gap-2 flex-wrap">
                          <span>{ev.league}</span>
                          {ev.home_record && <span className="text-zinc-400 font-mono lowercase normal-case">{ev.home_record}</span>}
                          {ev.home_ppg    && <span className="text-zinc-400 font-mono normal-case">{ev.home_ppg.toFixed(1)} ppg</span>}
                          {ev.status === "in" && (
                            <span className="text-red-400 flex items-center gap-1">
                              <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse inline-block" />
                              EN VIVO
                            </span>
                          )}
                        </div>
                      </div>
                      {ev.match_date && (
                        <div className="text-[11px] text-zinc-500 font-mono flex-shrink-0">
                          {format(new Date(ev.match_date), "dd MMM · HH:mm", { locale: es })}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ─── SelectionBuilder ──────────────────────────────────────── */

function SelectionBuilder({ marketConfig, home, away, value, onChange }) {
  const t = marketConfig?.type;

  if (t === "winner_1x2") {
    return <Grid3 value={value.pick} options={[
      { k: "home", l: home, sub: "Local" },
      { k: "draw", l: "Empate", sub: "X" },
      { k: "away", l: away, sub: "Visitante" },
    ]} onChange={pick => onChange({ pick })} />;
  }
  if (t === "winner_ml") {
    return <Grid2 value={value.pick} options={[
      { k: "home", l: home, sub: "Local" },
      { k: "away", l: away, sub: "Visitante" },
    ]} onChange={pick => onChange({ pick })} />;
  }
  if (t === "double_chance") {
    return <Grid3 value={value.pick} options={[
      { k: "1X", l: "1X", sub: `${home} o Empate` },
      { k: "12", l: "12", sub: `${home} o ${away}` },
      { k: "X2", l: "X2", sub: `Empate o ${away}` },
    ]} onChange={pick => onChange({ pick })} />;
  }
  if (t === "btts") {
    return <Grid2 value={value.pick} options={[
      { k: "yes", l: "Sí", sub: "Ambos marcan" },
      { k: "no", l: "No", sub: "Al menos uno no marca" },
    ]} onChange={pick => onChange({ pick })} />;
  }
  if (t === "total") {
    return (
      <div className="space-y-3">
        <div>
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider font-bold mb-2">Línea</div>
          <div className="flex flex-wrap gap-2">
            {marketConfig.lines.map(line => (
              <button key={line} type="button" onClick={() => onChange({ ...value, line })} data-testid={`admin-line-${line}`}
                className={`px-3.5 py-2 rounded-lg border text-sm font-semibold font-mono transition-all ${value.line === line ? "bg-yellow-500/10 border-yellow-500/30 text-yellow-400" : "bg-white/[0.02] border-white/[0.06] text-zinc-300 hover:border-white/[0.15]"}`}>
                {line}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider font-bold mb-2">Dirección</div>
          <Grid2 value={value.direction} options={[
            { k: "over",  l: "Más de",   sub: value.line != null ? `Over ${value.line}` : "—" },
            { k: "under", l: "Menos de", sub: value.line != null ? `Under ${value.line}` : "—" },
          ]} onChange={direction => onChange({ ...value, direction })} />
        </div>
      </div>
    );
  }
  if (t === "handicap") {
    return (
      <div className="space-y-3">
        <div>
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider font-bold mb-2">Equipo</div>
          <Grid2 value={value.team} options={[
            { k: "home", l: home, sub: "Local" },
            { k: "away", l: away, sub: "Visitante" },
          ]} onChange={team => onChange({ ...value, team })} />
        </div>
        <div>
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider font-bold mb-2">Línea</div>
          <div className="flex flex-wrap gap-2">
            {marketConfig.lines.map(line => {
              const sign = line > 0 ? "+" : "";
              return (
                <button key={line} type="button" onClick={() => onChange({ ...value, line })} data-testid={`admin-handicap-${line}`}
                  className={`px-3.5 py-2 rounded-lg border text-sm font-semibold font-mono transition-all ${value.line === line ? "bg-yellow-500/10 border-yellow-500/30 text-yellow-400" : "bg-white/[0.02] border-white/[0.06] text-zinc-300 hover:border-white/[0.15]"}`}>
                  {sign}{line}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }
  if (t === "half_winner") {
    return <Grid3 value={value.pick} options={[
      { k: "home", l: home, sub: `Local (${marketConfig.half} mitad)` },
      { k: "draw", l: "Empate", sub: `${marketConfig.half} mitad` },
      { k: "away", l: away, sub: `Visitante (${marketConfig.half} mitad)` },
    ]} onChange={pick => onChange({ pick })} />;
  }
  if (t === "exact_score") {
    const scores = marketConfig.scores || ["1-0","0-0","1-1","2-1","2-0"];
    return (
      <div className="space-y-3">
        <div className="flex flex-wrap gap-2">
          {scores.map(s => (
            <button key={s} type="button" onClick={() => onChange({ score: s })}
              data-testid={`admin-score-${s}`}
              className={`px-3.5 py-2 rounded-lg border text-sm font-semibold font-mono transition-all ${
                value.score === s ? "bg-yellow-500/10 border-yellow-500/30 text-yellow-400" : "bg-white/[0.02] border-white/[0.06] text-zinc-300 hover:border-white/[0.15]"
              }`}>
              {s}
            </button>
          ))}
        </div>
        <input className="input-field w-full text-sm" placeholder="Otro resultado (ej. 3-2)..."
          value={value.score && !scores.includes(value.score) ? value.score : ""}
          onChange={e => onChange({ score: e.target.value })}
          data-testid="admin-score-custom" />
      </div>
    );
  }
  if (t === "yn") {
    return <Grid2 value={value.pick} options={[
      { k: "yes", l: "Sí", sub: "Ocurre" },
      { k: "no", l: "No", sub: "No ocurre" },
    ]} onChange={pick => onChange({ pick })} />;
  }
  if (t === "margin") {
    const ranges = marketConfig.ranges || ["1-5 pts","6-10 pts","11+ pts"];
    return (
      <div className="flex flex-wrap gap-2">
        {ranges.map(r => (
          <button key={r} type="button" onClick={() => onChange({ range: r })}
            data-testid={`admin-margin-${r}`}
            className={`px-3.5 py-2 rounded-lg border text-sm font-semibold transition-all ${
              value.range === r ? "bg-yellow-500/10 border-yellow-500/30 text-yellow-400" : "bg-white/[0.02] border-white/[0.06] text-zinc-300 hover:border-white/[0.15]"
            }`}>
            {r}
          </button>
        ))}
      </div>
    );
  }
  if (t === "player_ou") {
    const line = marketConfig.line || 25.5;
    return <Grid2 value={value.direction} options={[
      { k: "over", l: `Over ${line} pts`, sub: "Supera el total" },
      { k: "under", l: `Under ${line} pts`, sub: "Por debajo del total" },
    ]} onChange={direction => onChange({ direction })} />;
  }
  return <div className="text-xs text-zinc-500">Selecciona un mercado</div>;
}

function Grid2({ value, options, onChange }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {options.map(o => (
        <button key={o.k} type="button" onClick={() => onChange(o.k)} data-testid={`admin-option-${o.k}`}
          className={`flex flex-col gap-0.5 px-4 py-3 rounded-xl border text-left transition-all ${value === o.k ? "bg-yellow-500/10 border-yellow-500/30 text-yellow-400" : "bg-white/[0.02] border-white/[0.06] text-zinc-300 hover:border-white/[0.15]"}`}>
          <span className="font-semibold text-sm truncate w-full">{o.l}</span>
          {o.sub && <span className="text-[10px] text-zinc-500 uppercase tracking-wider">{o.sub}</span>}
        </button>
      ))}
    </div>
  );
}

function Grid3({ value, options, onChange }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      {options.map(o => (
        <button key={o.k} type="button" onClick={() => onChange(o.k)} data-testid={`admin-option-${o.k}`}
          className={`flex flex-col gap-0.5 px-3 py-3 rounded-xl border text-left transition-all ${value === o.k ? "bg-yellow-500/10 border-yellow-500/30 text-yellow-400" : "bg-white/[0.02] border-white/[0.06] text-zinc-300 hover:border-white/[0.15]"}`}>
          <span className="font-semibold text-sm truncate w-full">{o.l}</span>
          {o.sub && <span className="text-[10px] text-zinc-500 uppercase tracking-wider">{o.sub}</span>}
        </button>
      ))}
    </div>
  );
}
